import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, HelpCircle, ChevronDown, BookOpen, MessageCircleQuestion } from 'lucide-react';
import { adminHelp, clientHelp } from './helpContent';
import { diagramas } from './diagramas';

interface HelpPageProps {
  variant?: 'admin' | 'client';
}

export function HelpPage({ variant = 'admin' }: HelpPageProps) {
  const navigate = useNavigate();
  const content = variant === 'client' ? clientHelp : adminHelp;
  const standalone = variant === 'admin';

  const [busca, setBusca] = useState('');
  const [abertas, setAbertas] = useState<Set<number>>(new Set());

  const termo = busca.trim().toLowerCase();

  const faqFiltradas = content.faq.filter(
    (f) =>
      !termo ||
      f.pergunta.toLowerCase().includes(termo) ||
      f.resposta.toLowerCase().includes(termo) ||
      f.categoria.toLowerCase().includes(termo)
  );

  const toggleFaq = (index: number) => {
    setAbertas((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  return (
    <div className={standalone ? 'min-h-screen bg-background' : ''}>
      {standalone && (
        <header className="sticky top-0 z-50 bg-card/70 backdrop-blur-xl border-b border-border/50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                aria-label="Voltar"
                className="p-2 rounded-lg hover:bg-muted transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-foreground" />
              </button>
              <div className="flex items-center gap-2">
                <HelpCircle className="w-6 h-6 text-primary" />
                <h1 className="text-xl font-bold text-foreground">{content.titulo}</h1>
              </div>
            </div>
          </div>
        </header>
      )}

      <main
        className={`max-w-4xl mx-auto px-6 ${standalone ? 'py-8' : 'py-2'} space-y-10`}
      >
        {!standalone && (
          <div className="flex items-center gap-2 pt-2">
            <HelpCircle className="w-6 h-6 text-primary" />
            <h1 className="text-xl font-bold text-foreground">{content.titulo}</h1>
          </div>
        )}

        {/* Busca */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <input
            type="search"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar no manual e nas perguntas frequentes..."
            aria-label="Buscar na central de ajuda"
            className="w-full pl-12 pr-4 py-3 bg-card border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* FAQ */}
        <section aria-labelledby="faq-title">
          <div className="flex items-center gap-2 mb-4">
            <MessageCircleQuestion className="w-5 h-5 text-primary" />
            <h2 id="faq-title" className="text-lg font-semibold text-foreground">
              Perguntas Frequentes
            </h2>
          </div>

          {faqFiltradas.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              Nenhum resultado encontrado para "{busca}".
            </p>
          ) : (
            <div className="space-y-3">
              {faqFiltradas.map((f, i) => {
                const aberto = abertas.has(i);
                return (
                  <div
                    key={f.pergunta}
                    className="bg-card border border-border rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() => toggleFaq(i)}
                      aria-expanded={aberto}
                      aria-controls={`faq-resposta-${i}`}
                      className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-muted/40 transition-colors"
                    >
                      <span className="flex-1">
                        <span className="block text-foreground font-medium">{f.pergunta}</span>
                        <span className="block text-xs text-muted-foreground mt-0.5">{f.categoria}</span>
                      </span>
                      <ChevronDown
                        className={`w-5 h-5 text-muted-foreground shrink-0 transition-transform ${
                          aberto ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    <div
                      id={`faq-resposta-${i}`}
                      role="region"
                      hidden={!aberto}
                      className="px-5 pb-4 text-sm text-muted-foreground leading-relaxed"
                    >
                      {f.resposta}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Manual */}
        <section aria-labelledby="manual-title">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="w-5 h-5 text-primary" />
            <h2 id="manual-title" className="text-lg font-semibold text-foreground">
              Manual de Uso
            </h2>
          </div>

          <div className="grid gap-6">
            {content.manual.map((s) => {
              const Diagrama = s.diagrama ? diagramas[s.diagrama] : undefined;
              return (
                <div key={s.id} className="bg-card border border-border rounded-xl p-6">
                  <h3 className="font-semibold text-foreground mb-3">
                    {s.emoji} {s.titulo}
                  </h3>

                  {Diagrama && (
                    <div className="mb-4 p-4 bg-muted/30 border border-border/50 rounded-lg">
                      <Diagrama />
                    </div>
                  )}

                  <ul className="space-y-3">
                    {s.topicos.map((t) => (
                      <li key={t.titulo} className="text-sm">
                        <span className="font-medium text-foreground">{t.titulo}</span>
                        <span className="text-muted-foreground"> — {t.descricao}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </section>
      </main>
    </div>
  );
}
